import clsx, { type ClassValue } from 'clsx';
import { format, differenceInCalendarDays } from 'date-fns';

/**
 * Combine class names with clsx.
 */
export function cn(...classes: ClassValue[]) {
  return clsx(...classes);
}

/**
 * Format a date to YYYY-MM-DD.
 */
export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return '-';
  return format(new Date(date), 'yyyy-MM-dd');
}

/**
 * Format a date to YYYY-MM-DD HH:mm.
 */
export function formatDateTime(date: string | Date | null | undefined): string {
  if (!date) return '-';
  return format(new Date(date), 'yyyy-MM-dd HH:mm');
}

/**
 * Format a number with comma separators.
 */
export function formatNumber(n: number | null | undefined): string {
  if (n == null) return '-';
  return n.toLocaleString('ko-KR');
}

/**
 * Returns Tailwind classes for project/activity status badge colors.
 */
export function getStatusColor(
  status: string,
): { bg: string; text: string } {
  switch (status) {
    case 'draft':
      return { bg: 'bg-gray-100 dark:bg-gray-700/40', text: 'text-gray-600 dark:text-gray-300' };
    case 'waiting':
    case 'pending':
      return { bg: 'bg-yellow-100 dark:bg-yellow-900/40', text: 'text-yellow-800 dark:text-yellow-300' };
    case 'planning':
      return { bg: 'bg-sky-100 dark:bg-sky-900/40', text: 'text-sky-700 dark:text-sky-300' };
    case 'in_progress':
      return { bg: 'bg-blue-100 dark:bg-blue-900/40', text: 'text-blue-700 dark:text-blue-300' };
    case 'completed':
      return { bg: 'bg-green-100 dark:bg-green-900/40', text: 'text-green-700 dark:text-green-300' };
    case 'closed':
      return { bg: 'bg-purple-100 dark:bg-purple-900/40', text: 'text-purple-700 dark:text-purple-300' };
    case 'cancelled':
      return { bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-700 dark:text-red-300' };
    default:
      return { bg: 'bg-gray-100 dark:bg-gray-700/40', text: 'text-gray-600 dark:text-gray-300' };
  }
}

/**
 * Returns Tailwind classes for effort level colors.
 */
export function getEffortColor(
  level: string,
): { bg: string; text: string } {
  switch (level) {
    case 'very_low':
      return { bg: 'bg-gray-100 dark:bg-gray-700/40', text: 'text-gray-600 dark:text-gray-300' };
    case 'low':
      return { bg: 'bg-blue-100 dark:bg-blue-900/40', text: 'text-blue-700 dark:text-blue-300' };
    case 'normal':
      return { bg: 'bg-green-100 dark:bg-green-900/40', text: 'text-green-700 dark:text-green-300' };
    case 'high':
      return { bg: 'bg-orange-100 dark:bg-orange-900/40', text: 'text-orange-700 dark:text-orange-300' };
    case 'very_high':
      return { bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-700 dark:text-red-300' };
    default:
      return { bg: 'bg-gray-100 dark:bg-gray-700/40', text: 'text-gray-600 dark:text-gray-300' };
  }
}

/**
 * Returns D-day text like "D-3", "D-Day", "D+5".
 */
export function getDDayText(targetDate: string | Date): string {
  const target = new Date(targetDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);

  const diff = differenceInCalendarDays(target, today);

  if (diff === 0) return 'D-Day';
  if (diff > 0) return `D-${diff}`;
  return `D+${Math.abs(diff)}`;
}

/**
 * Returns Tailwind text color class based on D-day urgency.
 * - 0-1 days: red (urgent)
 * - 2-3 days: orange
 * - 4-7 days: yellow
 * - 8+ days: default gray
 */
export function getDDayColor(dDay: number): string {
  const absDays = Math.abs(dDay);
  if (dDay <= 0) return 'text-red-600';
  if (absDays <= 1) return 'text-red-600';
  if (absDays <= 3) return 'text-orange-500';
  if (absDays <= 7) return 'text-yellow-600';
  return 'text-gray-500';
}

/**
 * Copy text to the clipboard.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
